import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { Slot } from "@radix-ui/react-slot";
import { Box, Skeleton, Text } from "@hightouch/htui";
import {
  MediaDetailHeader,
  MediaDetailHeaderSkeleton,
  MediaDetailMediaSkeleton,
} from "src/components/media-detail";
import type { AdForPrompt } from "src/pages/ad-studio/insights/ad-action-prompt";
import { AdDailyChart } from "src/pages/ad-studio/insights/ad-daily-chart";
import {
  type MeasuredSizes,
  promptCreativeOf,
  selectedCreativeOf,
} from "src/pages/ad-studio/insights/ad-creatives";
import { AdMediaItem } from "src/pages/ad-studio/insights/ad-media-item";
import { CreativeCarouselControls } from "src/pages/ad-studio/insights/creative-carousel-controls";
import { AdNotFound } from "src/pages/ad-studio/insights/ad-not-found";
import { CreativeTagsSection } from "src/pages/ad-studio/insights/creative-tags-section";
import { EntityTiles } from "src/pages/ad-studio/insights/entity-tiles";
import { FailureAlert } from "src/pages/ad-studio/insights/performance/failure-alert";
import type {
  AdDrilldownFailure,
  AdDrilldownParentAction,
  AdDrilldownState,
  CreativeChoice,
  FoundAd,
  InsightsQueryResult,
  LoadedSize,
  PickedCreative,
  SelectedAd,
  SelectedCreative,
  SuggestedActionsOffer,
} from "src/pages/ad-studio/insights/performance/types";
import { mapQueryResult } from "src/pages/ad-studio/insights/performance/utils/query-result";
import type {
  EntityCardMedia,
  ShownCardMedia,
} from "src/pages/ad-studio/insights/performance/view/types";
import { toEntityCardMedia } from "src/pages/ad-studio/insights/performance/view/utils";
import { TilesSkeleton } from "src/pages/ad-studio/insights/tiles-skeleton";
import { AdSuggestedActionsRowSkeleton } from "src/pages/ad-studio/insights/ad-suggested-actions-row";
import { SuggestedActionsSection } from "src/pages/ad-studio/insights/suggested-actions-section";
import { readMediaSize } from "src/pages/ad-studio/insights/shared/creative-dimensions";
import { useSuggestedActions } from "src/pages/ad-studio/insights/use-suggested-actions";

type Selected = InsightsQueryResult<SelectedCreative, AdDrilldownFailure>;

type Shown = InsightsQueryResult<
  { title: string; position: EntityCardMedia },
  AdDrilldownFailure
>;

type Suggestions =
  | { kind: "not-offered" }
  | { kind: "waiting" }
  | { kind: "offered"; ad: SelectedAd };

type AdDrilldownContextValue = Omit<
  AdDrilldownState,
  "media" | "ad" | "suggestedActions"
> & {
  reportSize: (report: { creativeId: string; size: LoadedSize }) => void;
  shown: Shown;
  pickCreative: (choice: PickedCreative) => void;
  suggestions: Suggestions;
};

const AdDrilldownContext = createContext<AdDrilldownContextValue | undefined>(
  undefined,
);

const useAdDrilldownContext = (part: string) => {
  const context = useContext(AdDrilldownContext);
  if (context === undefined) {
    throw new Error(
      `\`AdDrilldownPrimitive.${part}\` must be used within \`AdDrilldownPrimitive.Root\``,
    );
  }
  return context;
};

type CreativeControl =
  | {
      creative: CreativeChoice;
      onCreativeChange: (choice: PickedCreative) => void;
    }
  | { creative?: never; onCreativeChange?: never };

type RootProps = AdDrilldownState & CreativeControl & { children?: ReactNode };

const toSuggestions = ({
  offer,
  ad,
  selected,
}: {
  offer: SuggestedActionsOffer;
  ad: AdDrilldownState["ad"];
  selected: Selected;
}): Suggestions => {
  switch (offer) {
    case "flag-off":
    case "channel-without-actions": {
      return { kind: "not-offered" };
    }
    case "offered": {
      return offeredOnceLoaded({ ad, selected });
    }
    default: {
      return offer satisfies never;
    }
  }
};

const offeredOnceLoaded = ({
  ad,
  selected,
}: {
  ad: AdDrilldownState["ad"];
  selected: Selected;
}): Suggestions => {
  switch (ad.status) {
    case "loading":
    case "failed": {
      return { kind: "waiting" };
    }
    case "loaded": {
      switch (selected.status) {
        case "loading":
        case "failed": {
          return { kind: "waiting" };
        }
        case "loaded": {
          return {
            kind: "offered",
            ad: { ...ad.value, selectedCreative: selected.value },
          };
        }
        default: {
          return selected satisfies never;
        }
      }
    }
    default: {
      return ad satisfies never;
    }
  }
};

const Root = ({
  header,
  media,
  metrics,
  daily,
  tags,
  ad,
  suggestedActions,
  creative: controlledChoice,
  onCreativeChange,
  children,
}: RootProps) => {
  const [sizes, setSizes] = useState<MeasuredSizes>(new Map());
  const reportSize = useCallback(
    ({ creativeId, size }: { creativeId: string; size: LoadedSize }) =>
      setSizes((previous) => new Map(previous).set(creativeId, size)),
    [],
  );
  const [privateChoice, setPrivateChoice] = useState<CreativeChoice>({
    kind: "first-creative",
  });
  const choice = controlledChoice ?? privateChoice;
  const shown: Shown = mapQueryResult(media, (preview) => ({
    title: preview.title,
    position: toEntityCardMedia({ media: preview.media, choice }),
  }));
  const selected: Selected = mapQueryResult(shown, ({ position }) =>
    selectedCreativeOf({ position, sizes }),
  );
  return (
    <AdDrilldownContext.Provider
      value={{
        header,
        metrics,
        daily,
        tags,
        reportSize,
        shown,
        pickCreative: onCreativeChange ?? setPrivateChoice,
        suggestions: toSuggestions({ offer: suggestedActions, ad, selected }),
      }}
    >
      {children}
    </AdDrilldownContext.Provider>
  );
};

const RegionFailure = ({ failure }: { failure: AdDrilldownFailure }) =>
  failure.cause === "ad-not-found" ? null : <FailureAlert failure={failure} />;

const loadedSizeOf = (target: EventTarget): LoadedSize =>
  target instanceof HTMLImageElement || target instanceof HTMLVideoElement
    ? readMediaSize(target)
    : { kind: "load-failed" };

/** Reports the size of the creative's file once the image or video it wraps has loaded, or that it failed to. */
const MediaFrame = ({
  creativeId,
  children,
}: {
  creativeId: string;
  children?: ReactNode;
}) => {
  const { reportSize } = useAdDrilldownContext("MediaFrame");
  return (
    <Slot
      onLoad={(event) =>
        reportSize({ creativeId, size: loadedSizeOf(event.currentTarget) })
      }
      onLoadedMetadata={(event) =>
        reportSize({ creativeId, size: loadedSizeOf(event.currentTarget) })
      }
      onError={() => reportSize({ creativeId, size: { kind: "load-failed" } })}
    >
      {children}
    </Slot>
  );
};

const Header = () => {
  const { header } = useAdDrilldownContext("Header");
  switch (header.status) {
    case "loading": {
      return <MediaDetailHeaderSkeleton />;
    }
    case "failed": {
      return <HeaderFailure failure={header.failure} />;
    }
    case "loaded": {
      return (
        <MediaDetailHeader
          title={header.value.title}
          metaTags={header.value.tags}
          statuses={header.value.statuses}
          notes={header.value.notes}
        />
      );
    }
    default: {
      return header satisfies never;
    }
  }
};

const HeaderFailure = ({ failure }: { failure: AdDrilldownFailure }) =>
  failure.cause === "ad-not-found" ? (
    <AdNotFound heading={failure.heading} />
  ) : (
    <FailureAlert failure={failure} />
  );

/** A header titled by the group the ad was opened in; the ad's own name moves into the subtitle. */
const InGroupHeader = ({ children }: { children: string }) => {
  const { header } = useAdDrilldownContext("InGroupHeader");
  switch (header.status) {
    case "loading": {
      return <MediaDetailHeader title={children} />;
    }
    case "failed": {
      return (
        <>
          <MediaDetailHeader title={children} />
          <HeaderFailure failure={header.failure} />
        </>
      );
    }
    case "loaded": {
      return (
        <MediaDetailHeader
          title={children}
          metaTags={[header.value.title, ...header.value.tags]}
          statuses={header.value.statuses}
          notes={header.value.notes}
        />
      );
    }
    default: {
      return header satisfies never;
    }
  }
};

const Media = () => {
  const { shown } = useAdDrilldownContext("Media");
  switch (shown.status) {
    case "loading": {
      return <MediaDetailMediaSkeleton />;
    }
    case "failed": {
      return <RegionFailure failure={shown.failure} />;
    }
    case "loaded": {
      return (
        <ShownCreative
          title={shown.value.title}
          position={shown.value.position}
        />
      );
    }
    default: {
      return shown satisfies never;
    }
  }
};

const ShownCarousel = ({ position }: { position: ShownCardMedia }) => {
  const { pickCreative } = useAdDrilldownContext("Media");
  switch (position.kind) {
    case "single": {
      return null;
    }
    case "carousel": {
      return (
        <CreativeCarouselControls
          tone="on-surface"
          carousel={position}
          performAction={(action) => {
            switch (action.kind) {
              case "EntityCardAction/stepped-media": {
                return pickCreative(action.choice);
              }
              default: {
                return action.kind satisfies never;
              }
            }
          }}
        />
      );
    }
    default: {
      return position satisfies never;
    }
  }
};

const ShownCreative = ({
  title,
  position,
}: {
  title: string;
  position: EntityCardMedia;
}) => {
  switch (position.kind) {
    case "no-media": {
      return (
        <Text size="sm" color="text.tertiary">
          No preview
        </Text>
      );
    }
    case "single":
    case "carousel": {
      return (
        <Box
          position="relative"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <MediaFrame
            key={position.shown.creativeId}
            creativeId={position.shown.creativeId}
          >
            <AdMediaItem item={position.shown.item} title={title} />
          </MediaFrame>
          <ShownCarousel position={position} />
        </Box>
      );
    }
    default: {
      return position satisfies never;
    }
  }
};

const Metrics = () => {
  const { metrics } = useAdDrilldownContext("Metrics");
  switch (metrics.status) {
    case "loading": {
      return (
        <Skeleton isLoading>
          <TilesSkeleton />
        </Skeleton>
      );
    }
    case "failed": {
      return <RegionFailure failure={metrics.failure} />;
    }
    case "loaded": {
      return <EntityTiles tiles={metrics.value} />;
    }
    default: {
      return metrics satisfies never;
    }
  }
};

type TaggedScope = { kind: "one"; creativeId: string } | { kind: "all" };

const taggedScopeOf = (shown: Shown): TaggedScope => {
  if (shown.status !== "loaded") {
    return { kind: "all" };
  }
  const { position } = shown.value;
  switch (position.kind) {
    case "single":
    case "carousel": {
      return { kind: "one", creativeId: position.shown.creativeId };
    }
    case "no-media": {
      return { kind: "all" };
    }
    default: {
      return position satisfies never;
    }
  }
};

const Tags = () => {
  const { tags, shown } = useAdDrilldownContext("Tags");
  return (
    <CreativeTagsSection
      fivetranSyncId={tags.syncId}
      adId={tags.adId}
      creative={taggedScopeOf(shown)}
    />
  );
};

const SuggestedActions = ({
  performParentAction,
}: {
  performParentAction: (action: AdDrilldownParentAction) => void;
}) => {
  const { suggestions } = useAdDrilldownContext("SuggestedActions");
  switch (suggestions.kind) {
    case "not-offered":
    case "waiting": {
      return null;
    }
    case "offered": {
      return (
        <OfferedSuggestedActions
          ad={suggestions.ad}
          performParentAction={performParentAction}
        />
      );
    }
    default: {
      return suggestions satisfies never;
    }
  }
};

const OfferedSuggestedActions = ({
  ad,
  performParentAction,
}: {
  ad: SelectedAd;
  performParentAction: (action: AdDrilldownParentAction) => void;
}) => {
  const prompt = promptCreativeOf(ad.selectedCreative);
  switch (prompt.kind) {
    case "measuring": {
      return <AdSuggestedActionsRowSkeleton />;
    }
    case "ready": {
      return (
        <ReadySuggestedActions
          ad={{ ...ad.ad, creative: prompt.creative }}
          fatigue={ad.fatigue}
          performParentAction={performParentAction}
        />
      );
    }
    default: {
      return prompt satisfies never;
    }
  }
};

const ReadySuggestedActions = ({
  ad,
  fatigue,
  performParentAction,
}: {
  ad: AdForPrompt;
  fatigue: FoundAd["fatigue"];
  performParentAction: (action: AdDrilldownParentAction) => void;
}) => {
  const suggestions = useSuggestedActions({
    ad,
    fatigue,
    performParentAction: (pick) =>
      performParentAction({
        kind: "AdDrilldownParentAction/picked-suggested-action",
        pick,
      }),
  });
  return (
    <SuggestedActionsSection
      suggestions={suggestions.state}
      performParentAction={(selected) => {
        switch (selected.kind) {
          case "SuggestedActionsSectionParentAction/selected": {
            return suggestions.performAction({
              kind: "SuggestedActionsAction/selected",
              action: selected.action,
            });
          }
          default: {
            return selected.kind satisfies never;
          }
        }
      }}
    />
  );
};

const Daily = () => {
  const { daily } = useAdDrilldownContext("Daily");
  return <AdDailyChart daily={daily} />;
};

export const AdDrilldownPrimitive = {
  Root,
  MediaFrame,
  Header,
  InGroupHeader,
  Media,
  Metrics,
  Daily,
  Tags,
  SuggestedActions,
};
